import { motion } from "motion/react";
import PropTypes from "prop-types";

import { styled } from "styled-components";

import { colors } from "UI/colors";

import useNavMobileBreakpoint from "./useNavMobileBreakpoint";

const iconInitial = { opacity: 0, scale: 0.8 };

const getLabelVariants = (index) => ({
  hidden: { opacity: 0, scale: 1 },
  show: {
    opacity: 0.9,
    scale: 1,
    transition: {
      duration: 0.3,
      delay: index * 0.12 + 0.18, // Base stagger delay + additional delay for labels
    },
  },
});

NavItem.propTypes = {
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  isActive: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  color: PropTypes.string,
  bgColor: PropTypes.string,
  iconX: PropTypes.number,
  index: PropTypes.number,
};

export default function NavItem({
  icon,
  label,
  isActive,
  color,
  bgColor,
  onClick,
  iconX,
  index = 0,
}) {
  const mobileBreakpoint = useNavMobileBreakpoint();
  return (
    <Wrapper $isActive={isActive} onClick={onClick}>
      <IconWrapper
        $color={color}
        $bgColor={bgColor}
        $isActive={isActive}
        initial={iconInitial}
        animate={{
          opacity: 1,
          scale: [0.9, 1.1, 1],
          ...(isActive
            ? {
                outline: `4px solid ${color ?? colors.fnBlue}`,
                outlineOffset: ["2px", "3px", "4px", "5px", "4px"],
              }
            : {
                outline: "4px solid transparent",
                outlineOffset: "0",
              }),
        }}
        $mobileBreakpoint={mobileBreakpoint}
        transition={{
          type: "spring",
          duration: 0.2,
          delay: index * 0.05,
          outline: {
            duration: 0.1,
            type: "spring",
          },
          outlineOffset: {
            duration: 0.18,
            type: "spring",
          },
        }}
      >
        <TappyShrinkCustom
          tabIndex={-1}
          whileTap={{
            scale: 0.92,
          }}
        >
          <Icon
            className={icon}
            $iconX={iconX}
            $mobileBreakpoint={mobileBreakpoint}
          />
        </TappyShrinkCustom>
      </IconWrapper>
      <Label
        variants={getLabelVariants(index)}
        initial="hidden"
        animate="show"
        $mobileBreakpoint={mobileBreakpoint}
      >
        {label}
      </Label>
    </Wrapper>
  );
}

const Wrapper = styled(motion.li)`
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
`;

const IconWrapper = styled(motion.div)`
  width: 56px;
  height: 56px;
  border-radius: 100%;
  padding: 8px;
  flex-shrink: 0;
  flex-grow: 0;
  display: flex;
  color: ${({ $color }) => $color ?? colors.white};
  background: ${({ $bgColor }) => {
    const baseColor = $bgColor ?? colors.fnBlue;
    return `linear-gradient(135deg, ${baseColor}, ${baseColor}94)`;
  }};

  @media (max-width: ${({ $mobileBreakpoint }) => $mobileBreakpoint}px) {
    width: 38px;
    height: 38px;
  }
`;

const TappyShrinkCustom = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const Icon = styled.span`
  font-size: 34px;
  color: inherit;
  text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.1);
  transform: translateX(${({ $iconX }) => ($iconX ? `${$iconX}px` : "0")});

  @media (max-width: ${({ $mobileBreakpoint }) => $mobileBreakpoint}px) {
    font-size: 28px;
  }
`;

const Label = styled(motion.div)`
  text-transform: uppercase;
  margin-top: 9px;
  font-weight: 600;
  font-size: 13px;

  @media (max-width: ${({ $mobileBreakpoint }) => $mobileBreakpoint}px) {
    font-size: 11px;
  }
`;
